import React from 'react';
import { FileText, User, Pill } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const DoctorCreateForm = ({
  patientName, setPatientName,
  age, setAge,
  weight, setWeight,
  gender, setGender,
  allergies, setAllergies,
  currentMedications, setCurrentMedications,
  medicalHistory, setMedicalHistory,
  symptoms, setSymptoms,
  diagnosis, setDiagnosis,
  severity, setSeverity,
  additionalNotes, setAdditionalNotes,
  loading, handleGenerateSuggestions, t
}) => (
  <div className="space-y-6">
    <Card className="p-6">
      <h2 className="text-2xl font-bold text-blue-600 mb-4 flex items-center gap-2">
        <User className="w-6 h-6" />
        Patient Information
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="patientName">Patient Name *</Label>
          <Input id="patientName" value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder="Enter patient name" required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="age">Age *</Label>
            <Input id="age" type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Years" required />
          </div>
          <div>
            <Label htmlFor="weight">Weight (kg)</Label>
            <Input id="weight" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        <div>
          <Label htmlFor="gender">Gender</Label>
          <select id="gender" value={gender} onChange={(e) => setGender(e.target.value)} className="w-full px-3 py-2 border rounded-md">
            <option value="Male">{t('gender.male')}</option>
            <option value="Female">{t('gender.female')}</option>
            <option value="Other">{t('gender.other')}</option>
          </select>
        </div>
        <div>
          <Label htmlFor="allergies">Known Allergies</Label>
          <Input id="allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Comma-separated (e.g., Penicillin, Sulfa drugs)" />
        </div>
        <div>
          <Label htmlFor="currentMeds">Current Medications</Label>
          <Input id="currentMeds" value={currentMedications} onChange={(e) => setCurrentMedications(e.target.value)} placeholder="Comma-separated medications" />
        </div>
        <div>
          <Label htmlFor="medHistory">Medical History</Label>
          <Input id="medHistory" value={medicalHistory} onChange={(e) => setMedicalHistory(e.target.value)} placeholder="Comma-separated conditions" />
        </div>
      </div>
    </Card>

    <Card className="p-6">
      <h2 className="text-2xl font-bold text-blue-600 mb-4 flex items-center gap-2">
        <Pill className="w-6 h-6" />
        Diagnosis & Symptoms
      </h2>
      <div className="space-y-4">
        <div>
          <Label htmlFor="symptoms">Symptoms *</Label>
          <textarea id="symptoms" value={symptoms} onChange={(e) => setSymptoms(e.target.value)} placeholder="Comma-separated symptoms (e.g., Fever, Cough, Body ache)" className="w-full px-3 py-2 border rounded-md" rows="2" required />
        </div>
        <div>
          <Label htmlFor="diagnosis">Diagnosis *</Label>
          <Input id="diagnosis" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="e.g., Acute Upper Respiratory Tract Infection" required />
        </div>
        <div>
          <Label htmlFor="severity">Severity</Label>
          <select id="severity" value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-full px-3 py-2 border rounded-md">
            <option value="mild">Mild</option>
            <option value="moderate">Moderate</option>
            <option value="severe">Severe</option>
          </select>
        </div>
        <div>
          <Label htmlFor="notes">Additional Notes</Label>
          <textarea id="notes" value={additionalNotes} onChange={(e) => setAdditionalNotes(e.target.value)} placeholder="Any additional observations or patient concerns" className="w-full px-3 py-2 border rounded-md" rows="3" />
        </div>
      </div>
    </Card>

    <div className="flex justify-center">
      <Button onClick={handleGenerateSuggestions} disabled={loading || !patientName || !age || !symptoms || !diagnosis} size="lg" className="px-12 bg-blue-600 hover:bg-blue-700" data-testid="generate-prescription-btn">
        {loading ? (
          <span className="flex items-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Generating AI Suggestions...</span>
        ) : (
          <span className="flex items-center"><FileText className="w-5 h-5 mr-2" />Generate AI Prescription Suggestions</span>
        )}
      </Button>
    </div>
  </div>
);

export default DoctorCreateForm;
